import { db } from './firebaseConfig';
import { doc, setDoc, getDoc, addDoc, collection, onSnapshot } from 'firebase/firestore';

export interface TrackProgress {
  position: number;
  percent: number;
  completed: boolean;
  playCount: number;
  lastUpdated: string;
}
export type UserProgressMap = { [step: string]: TrackProgress };

export const saveUserProgress = async (userId: string, step: number | string, position: number) => {
  try {
    const userDocRef = doc(db, 'userProgress', userId);
    await setDoc(userDocRef, {
      [step.toString()]: {
        position,
        lastUpdated: new Date().toISOString()
      }
    }, { merge: true });
  } catch (error) {
    console.error('Error saving progress:', error);
  }
};

export const getUserProgress = async (userId: string): Promise<UserProgressMap | null> => {
  try {
    const userDocRef = doc(db, 'userProgress', userId);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      return docSnap.data() as UserProgressMap;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user progress:', error);
    return null;
  }
};

export const saveTrackPosition = async (
  userId: string, step: number | string,
  position: number, percent: number
) => {
  try {
    const userDocRef = doc(db, 'userProgress', userId);
    await setDoc(userDocRef, {
      [step.toString()]: {
        position,
        percent,
        lastUpdated: new Date().toISOString()
      }
    }, { merge: true });
  } catch (error) {
    console.error('Error saving track position:', error);
  }
};

export const markTrackCompleted = async (
  userId: string, step: number | string,
  currentPlayCount: number
) => {
  try {
    const userDocRef = doc(db, 'userProgress', userId);
    await setDoc(userDocRef, {
      [step.toString()]: {
        position: 0,
        percent: 100,
        completed: true,
        playCount: currentPlayCount + 1,
        lastUpdated: new Date().toISOString()
      }
    }, { merge: true });
  } catch (error) {
    console.error('Error marking track completed:', error);
  }
};

export const clearUserProgress = async (userId: string) => {
  try {
    const userDocRef = doc(db, 'userProgress', userId);
    await setDoc(userDocRef, {}, { merge: false });
  } catch (error) {
    console.error('Error clearing progress:', error);
  }
};

export type AppearanceMode = 'system' | 'light' | 'dark';

export const saveUserDarkModeSetting = async (userId: string, appearance: AppearanceMode) => {
  try {
    const userPreferencesDocRef = doc(db, 'userPreferences', userId);
    await setDoc(userPreferencesDocRef, {
      appearance,
      lastUpdated: new Date().toISOString()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving appearance setting:', error);
  }
};

export const getUserDarkModeSetting = async (userId: string): Promise<AppearanceMode | null> => {
  try {
    const userPreferencesDocRef = doc(db, 'userPreferences', userId);
    const docSnap = await getDoc(userPreferencesDocRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      // New string format
      if (data.appearance !== undefined) return data.appearance as AppearanceMode;
      // Old boolean format — migrate on next save
      if (data.darkMode !== undefined) return data.darkMode ? 'dark' : 'light';
    }
    return null;
  } catch (error) {
    console.error('Error getting appearance setting:', error);
    return null;
  }
};

export const getUserAccess = async (uid: string): Promise<boolean> => {
  try {
    const snap = await getDoc(doc(db, 'userAccess', uid));
    return snap.exists() && snap.data()?.hasAccess === true;
  } catch {
    return false;
  }
};

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

export const submitFeedback = async (uid: string, userEmail: string, message: string, isSubscriber: boolean): Promise<void> => {
  const plan = isSubscriber ? '$9.95/month' : '$55 one-time';
  const clientInfo = [
    `Stripe plan: ${plan}`,
    `Browser: ${navigator.userAgent}`,
    `Screen: ${window.screen.width}×${window.screen.height} (devicePixelRatio: ${window.devicePixelRatio})`,
    `Platform: ${navigator.platform}`,
    `URL: ${window.location.href}`,
  ].join('\n');

  const mailRef = collection(db, 'mail');
  await addDoc(mailRef, {
    to: 'technicalsupport@bbks.com',
    cc: userEmail,
    replyTo: 'technicalsupport@bbks.com',
    message: {
      subject: `FEEDBACK: SPANISH in 10 Minutes a Day AUDIO`,
      html: `<p><strong>From:</strong> ${escapeHtml(userEmail)}</p><p><strong>Plan:</strong> ${plan}</p><br/><p>${escapeHtml(message).replace(/\n/g, '<br/>')}</p><br/><hr/><p style="font-size:12px;color:#888;"><strong>Client info:</strong><br/>${escapeHtml(clientInfo).replace(/\n/g, '<br/>')}</p>`,
    },
  });
};

export const createCheckoutSession = async (uid: string, priceId: string, email?: string): Promise<string> => {
  const sessionsRef = collection(db, 'customers', uid, 'checkout_sessions');
  const docRef = await addDoc(sessionsRef, {
    price: priceId,
    success_url: window.location.origin,
    cancel_url: window.location.origin,
    ...(email ? { customer_email: email } : {}),
  });
  return new Promise((resolve, reject) => {
    const unsub = onSnapshot(docRef, (snap) => {
      const data = snap.data();
      if (data?.url) { unsub(); resolve(data.url); }
      if (data?.error) { unsub(); reject(new Error(data.error.message)); }
    });
  });
};
